import React, { useRef, useEffect, useState, useContext } from "react";
import { useToast } from "@chakra-ui/react";
import Peer from "simple-peer";
import process from "process";
import { useAuth } from "../context/AuthContext";
import { SocketContext } from "../context/SocketContext";
import { useCallPopup } from "../context/CallPopupContext";
import { getCallReceiverName } from "../logics/callLogic";
import incomingCallAudioFile from "../sounds/incoming_call.mp3";
import VideoCallModal from "../components/calls/VideoCallModal";
import AudioCallModal from "../components/calls/AudioCallModal";
window.process = process;

const VideoCallPopup = () => {
  const { currentUser } = useAuth();
  const { callChatData, isOpen, openCallPopup, closeCallPopup } =
    useCallPopup();

  const { socket } = useContext(SocketContext);
  const [stream, setStream] = useState();
  const [remoteUserName, setRemoteUserName] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isTalking, setIsTalking] = useState(false);
  const [callerSignal, setCallerSignal] = useState();
  const [callDirection, setCallDirection] = useState();
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [remoteUserMuted, setRemoteUserMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [videoCameras, setVideoCameras] = useState([]);
  const localUserVideoRef = useRef();
  const remoteUserVideoRef = useRef();
  const connectionRef = useRef();
  const callAudioRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const callRingTimerRef = useRef(null);
  const toast = useToast();
  const [callData, setCallData] = useState({
    callId: null,
    callType: null,
    chatId: null,
    initUser: null,
    status: "START_CALL",
  });
  const thisUser = { id: currentUser?.id, name: currentUser?.name };

  const handleIncomingCall = (data) => {
    if (data.from.id !== currentUser.id) {
      setRemoteUserName(data.from.name);
      setCallerSignal(data.signal);
      playIncomingCallAudio();
      setCallDirection("INCOMING");
      getLocalUserMedia(data.callType);
      openCallPopup();
      updateCallStatusToRinging(data.call_data.callId, data.chatId);
      setCallData((prevData) => {
        return {
          ...prevData,
          callId: data.call_data.callId,
          callType: data.callType,
          chatId: data.chatId,
          initUser: data.initUser,
          status: "INCOMING_CALL",
        };
      });
    }
  };

  const handleCallDeclined = (data) => {
    setCallData((prev) => ({ ...prev, status: "CALL_DECLINED" }));
    toast({
      title: `Call declined by ${data.from.name}`,
      status: "info",
      duration: 1500,
      isClosable: true,
    });
    handleCloseModal();
  };

  const handleCallEnded = (data) => {
    stopIncomingCallAudio();
    setCallData((prev) => ({ ...prev, status: "CALL_FINISHED" }));
    toast({
      title: "Call Ended",
      status: "info",
      duration: 1500,
      isClosable: true,
    });
    handleCloseModal();
  };

  const handleCallNotAnswered = (data) => {
    setCallData((prev) => ({ ...prev, status: "CALL_NOT_ANSWERED" }));
    handleCloseModal();
  };

  const handleCallFailed = (data) => {
    setCallData((prev) => ({ ...prev, status: "CALL_FAILED" }));
    handleCloseModal();
  };

  const handleRemoteCallRinging = (data) => {
    setCallData((prev) => ({ ...prev, status: "RINGING" }));
  };

  const handleRemoteUserMuted = (data) => {
    setRemoteUserMuted(data.isMuted);
  };

  const getVideoDevices = async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(
        (device) => device.kind === "videoinput"
      );
      console.log("videoDevices: ", videoCameras);
      setVideoCameras(videoDevices);
    } catch (error) {
      console.error("Error enumerating devices", error);
    }
  };

  const getLocalUserMedia = async (pCallType) => {
    try {
      const localUserStream = await navigator.mediaDevices.getUserMedia({
        video: pCallType === "Video" ? true : false,
        audio: true,
      });
      setStream(localUserStream);
      return localUserStream;
    } catch (error) {
      console.error("Failed to access media devices:", error);
      return null;
    }
  };

  // to stop media control
  const stopMediaTrack = () => {
    if (stream?.getTracks) {
      const currentStream = stream;
      currentStream.getTracks().forEach((track) => track.stop());
      if (connectionRef.current && !connectionRef.current.destroyed) {
        connectionRef.current.removeStream(stream);
      }
      setStream(null);
      if (localUserVideoRef && localUserVideoRef.current) {
        localUserVideoRef.current.srcObject = null;
      }
    }
  };

  useEffect(() => {
    if (callChatData?.chatId) {
      const initCall = async () => {
        getVideoDevices();
        const localStream = await getLocalUserMedia(callChatData.type);
        if (!localStream) return;

        setRemoteUserName(getCallReceiverName(callChatData, currentUser));
        setCallDirection("OUTGOING");
        setCallData((prevData) => {
          return {
            ...prevData,
            callType: callChatData.type,
            chatId: callChatData.chatId,
            initUser: thisUser,
            status: "CALLING",
          };
        });
        socket.emit("JOINED_CALL_ROOM", callChatData.chatId);
        handleInitiateCall(localStream);
      };
      initCall();
    }
  }, [isOpen, callChatData?.type]);

  useEffect(() => {
    socket.on("RECEIVE_CALL", handleIncomingCall);
    socket.on("CALL_DECLINED", handleCallDeclined);
    socket.on("CALL_ENDED", handleCallEnded);
    socket.on("CALL_NOT_ANSWERED", handleCallNotAnswered);
    socket.on("CALL_FAILED", handleCallFailed);
    socket.on("REMOTE_CALL_RINGING", handleRemoteCallRinging);
    socket.on("REMOTE_USER_MUTED", handleRemoteUserMuted);

    return () => {
      socket.off("RECEIVE_CALL", handleIncomingCall);
      socket.off("CALL_DECLINED", handleCallDeclined);
      socket.off("CALL_ENDED", handleCallEnded);
      socket.off("CALL_NOT_ANSWERED", handleCallNotAnswered);
      socket.off("CALL_FAILED", handleCallFailed);
      socket.off("CALL_ACCEPTED");
      socket.off("REMOTE_CALL_RINGING", handleRemoteCallRinging);
      socket.off("REMOTE_USER_MUTED", handleRemoteUserMuted);
    };
  }, [socket]);

  useEffect(() => {
    if (
      callData.status === "CALL_CONNECTED" ||
      callData.status === "YOU_DECLINED_THE_CALL" ||
      callData.status === "CALL_ENDED_BY_YOU" ||
      callData.status === "CALL_FINISHED"
    ) {
      stopIncomingCallAudio();
    }
    localStorage.setItem("callData", JSON.stringify(callData));
  }, [callData]);

  useEffect(() => {
    if (localUserVideoRef.current) {
      localUserVideoRef.current.srcObject = stream;
    }
  }, [stream]);

  const handleInitiateCall = (localStream) => {
    const peerInstance = new Peer({
      initiator: true,
      trickle: false,
      stream: localStream,
    });
    connectionRef.current = peerInstance;

    peerInstance.on("signal", (data) => {
      if (peerInstance && !peerInstance.destroyed) {
        const storedCallData = getLocalCallData();
        socket.emit("CALL_USER", {
          callType: storedCallData.callType,
          chatId: storedCallData.chatId,
          from: thisUser,
          signalData: data,
          initUser: storedCallData.initUser,
        });
        setCallData((prev) => ({ ...prev, status: "CALLING" }));
      } else {
        console.warn("Signaling data received after peer was destroyed");
      }
    });

    peerInstance.on("stream", (remoteStream) => {
      if (remoteUserVideoRef.current) {
        remoteUserVideoRef.current.srcObject = remoteStream;
      }
      setupAudioAnalysis(remoteStream);
      const storedCallData = getLocalCallData();
      if (storedCallData.callType === "Audio") {
        playRemoteAudioStream(remoteStream);
      }
    });

    peerInstance.on("close", () => {
      console.log("Peer connection closed");
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        connectionRef.current = null;
      }
    });

    // Handle errors
    peerInstance.on("error", (err) => {
      console.error("Peer error:", err);
    });

    peerInstance._pc.onconnectionstatechange = () => {
      console.log("Connection state:", peerInstance._pc.connectionState);
    };

    socket.on("CALL_ACCEPTED", (data) => {
      if (peerInstance && !peerInstance.destroyed) {
        setCallDirection("ONGOING");
        peerInstance.signal(data.remoteSignal);
        setCallData((prevData) => {
          return {
            ...prevData,
            callId: data.callId,
            status: "CALL_ACCEPTED",
          };
        });
      } else {
        console.warn("Signaling data accepted after peer was destroyed");
      }
    });
  };

  const handleAnswerCall = () => {
    stopIncomingCallAudio();
    setCallDirection("ONGOING");
    const peerInstance = new Peer({
      initiator: false,
      trickle: false,
      stream: stream,
    });
    peerInstance.on("signal", (data) => {
      const storedCallData = getLocalCallData();
      const answerCallPayload = {
        callId: storedCallData.callId,
        callType: storedCallData.callType,
        chatId: storedCallData.chatId,
        from: thisUser,
        signal: data,
      };
      socket.emit("ANSWER_CALL", answerCallPayload);
      socket.emit("JOINED_CALL_ROOM", storedCallData.chatId);
    });
    peerInstance.on("stream", (remoteStream) => {
      if (remoteUserVideoRef.current) {
        remoteUserVideoRef.current.srcObject = remoteStream;
      }
      setupAudioAnalysis(remoteStream);
      const storedCallData = getLocalCallData();
      if (storedCallData.callType === "Audio") {
        playRemoteAudioStream(remoteStream);
      }
    });
    peerInstance.signal(callerSignal);
    connectionRef.current = peerInstance;
    setCallData((prevData) => ({ ...prevData, status: "CALL_CONNECTED" }));
  };

  const handleDeclineCall = () => {
    stopIncomingCallAudio();
    const storedCallData = getLocalCallData();
    const declineCallPayload = {
      callId: storedCallData.callId,
      callType: storedCallData.callType,
      chatId: storedCallData.chatId,
      from: thisUser,
    };
    socket.emit("DECLINE_CALL", declineCallPayload);
    setCallData((prev) => ({ ...prev, status: "YOU_DECLINED_THE_CALL" }));
    handleCloseModal();
  };

  const handleMissedCall = () => {
    const storedCallData = getLocalCallData();
    const missedCallPayload = {
      callId: storedCallData.callId,
      callType: storedCallData.callType,
      chatId: storedCallData.chatId,
      from: thisUser,
    };
    socket.emit("MISSED_CALL", missedCallPayload);
    setCallData((prev) => ({ ...prev, status: "CALL_MISSED_BY_YOU" }));
    handleCloseModal();
  };

  const handleEndCall = () => {
    const storedCallData = getLocalCallData();
    const endCallPayload = {
      callId: storedCallData.callId,
      callType: storedCallData.callType,
      chatId: storedCallData.chatId,
      from: thisUser,
    };
    socket.emit("END_CALL", endCallPayload);
    if (connectionRef.current) {
      connectionRef.current.destroy();
    }
    setCallData((prev) => ({ ...prev, status: "CALL_ENDED_BY_YOU" }));
    handleCloseModal();
  };

  // to play incoming call audio
  const playIncomingCallAudio = () => {
    if (!callAudioRef.current) {
      callAudioRef.current = new Audio(incomingCallAudioFile);
    }

    callAudioRef.current.currentTime = 0;
    callAudioRef.current.loop = true;
    callAudioRef.current.play().catch((error) => {
      console.error("Error playing incoming call sound:", error);
    });

    if (callRingTimerRef.current) {
      clearTimeout(callRingTimerRef.current);
    }

    // to stop ring after 20 seconds if not answered
    callRingTimerRef.current = setTimeout(() => {
      stopIncomingCallAudio();
      const storedCallData = getLocalCallData();
      if (
        storedCallData?.status !== "CALL_CONNECTED" &&
        storedCallData?.status !== "YOU_DECLINED_THE_CALL" &&
        storedCallData?.status !== "CALL_ENDED_BY_YOU" &&
        storedCallData?.status !== "CALL_FINISHED"
      ) {
        handleMissedCall();
      }
    }, 20000); // 20 seconds
  };

  const stopIncomingCallAudio = () => {
    // to stop timer of 15 seconds
    if (callRingTimerRef.current) {
      clearTimeout(callRingTimerRef.current);
      callRingTimerRef.current = null;
    }

    // to stop ringing audio
    if (callAudioRef.current) {
      callAudioRef.current.pause();
      callAudioRef.current.currentTime = 0;
    }
  };

  // to update call status
  const updateCallStatusToRinging = async (pCallId, pChatId) => {
    const ringingStatusPayload = {
      callId: pCallId,
      chatId: pChatId,
      from: thisUser,
    };
    socket.emit("RINGING_CALL", ringingStatusPayload);
  };

  const getLocalCallData = () => {
    const localCallData = localStorage.getItem("callData");
    return localCallData ? JSON.parse(localCallData) : {};
  };

  // Function to analyze the audio stream and detect talking
  const setupAudioAnalysis = (userStream) => {
    if (audioContextRef.current) return; // Only set up once

    // Create AudioContext and AnalyserNode
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(userStream);
    source.connect(analyser);
    analyser.fftSize = 512;

    // Allocate array to hold audio data
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    // Function to analyze audio volume
    const detectTalking = () => {
      if (audioEnabled) {
        analyser.getByteFrequencyData(dataArray);
        const sum = dataArray.reduce((a, b) => a + b, 0);
        const averageVolume = sum / dataArray.length;

        // Determine if the user is talking based on average volume
        if (averageVolume > 30) {
          setIsTalking(true);
        } else {
          setIsTalking(false);
        }

        // Continue to monitor audio data
        requestAnimationFrame(detectTalking);
      } else {
        setIsTalking(false);
      }
    };
    // Start monitoring audio levels
    detectTalking();
  };

  const playRemoteAudioStream = (audioStream) => {
    const audioElement = new Audio();
    audioElement.srcObject = audioStream;
    audioElement.autoplay = true;
    audioElement.muted = false; // Ensure it's not muted on the client side
    audioElement.play().catch((error) => {
      console.log("Playback error:", error);
    });
  };

  const toggleAudio = () => {
    if (stream) {
      stream.getAudioTracks()[0].enabled = !audioEnabled;
      setAudioEnabled(!audioEnabled);
      const storedCallData = getLocalCallData();
      socket.emit("USER_MUTED", {
        isMuted: audioEnabled,
        chatId: storedCallData.chatId,
        from: thisUser,
      });
      toast({
        title: `Microphone ${audioEnabled ? "Muted" : "Unmuted"}`,
        status: "info",
        position: "top",
        duration: 1500,
        isClosable: true,
      });
    }
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn((prev) => !prev);
    toast({
      title: `Speaker ${isSpeakerOn ? "Off" : "On"}`,
      status: "info",
      position: "top",
      duration: 1500,
      isClosable: true,
    });
  };

  const toggleVideo = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      videoTrack.enabled = !videoEnabled;
      setVideoEnabled(videoTrack.enabled);
    }
  };

  const handleCloseModal = () => {
    stopMediaTrack();
    localStorage.removeItem("callData");
    setTimeout(() => {
      closeCallPopup();
    }, 300);
  };

  // Timer for ongoing call
  useEffect(() => {
    let intervalId;
    if (isOpen && callDirection === "ONGOING") {
      setCallDuration(0);
      intervalId = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isOpen, callDirection]);

  return (
    <>
      {callData.callType === "Audio" ? (
        <AudioCallModal
          isOpen={isOpen}
          callDirection={callDirection}
          remoteUserName={remoteUserName}
          callData={callData}
          callDuration={callDuration}
          remoteUserMuted={remoteUserMuted}
          isTalking={isTalking}
          audioEnabled={audioEnabled}
          toggleAudio={toggleAudio}
          toggleSpeaker={toggleSpeaker}
          isSpeakerOn={isSpeakerOn}
          handleAnswerCall={handleAnswerCall}
          handleEndCall={handleEndCall}
          handleDeclineCall={handleDeclineCall}
        />
      ) : (
        <VideoCallModal
          isOpen={isOpen}
          callDirection={callDirection}
          remoteUserName={remoteUserName}
          callData={callData}
          callDuration={callDuration}
          remoteUserMuted={remoteUserMuted}
          isTalking={isTalking}
          audioEnabled={audioEnabled}
          toggleAudio={toggleAudio}
          remoteUserVideoRef={remoteUserVideoRef}
          localUserVideoRef={localUserVideoRef}
          videoEnabled={videoEnabled}
          toggleVideo={toggleVideo}
          handleAnswerCall={handleAnswerCall}
          handleEndCall={handleEndCall}
          handleDeclineCall={handleDeclineCall}
        />
      )}
    </>
  );
};

export default VideoCallPopup;
